import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { moveOptionFocus, usePopover } from "./usePopover";

/**
 * A single-choice dropdown. The filter-bar pill, the topbar's company and site
 * selectors, and the in-form replacement for a native `<select>`.
 *
 * Not a native `<select>`, and there are now three things it has that a select
 * cannot: an option can carry a second line (which is what lets the site
 * selector show a site's company under its name), a long list can be searched,
 * and options can be grouped under headings. The cost is that the keyboard
 * behaviour is ours, so it is kept to what people actually press - see below.
 *
 * ── The three variants ──────────────────────────────────────────────────────
 *
 * `pill`  a filter bar's control. Auto width, tinted when it carries a choice.
 * `chip`  the topbar scope switchers. **Fixed width**, so switching from
 *         "Hyderabad" to "North Yard Distribution Centre" does not shuffle the
 *         whole topbar sideways. A long name truncates.
 * `field` a form control. Full width and the same 40px as `.input`, so a row of
 *         a name field and this one line up.
 *
 * ── The fixed variant ───────────────────────────────────────────────────────
 *
 * When there is only one option, the trigger renders **without a chevron**, with
 * a default cursor, and the click does nothing. It is not `disabled`.
 *
 * That distinction is the whole point. A greyed-out control says "you may not
 * do this", which is wrong and slightly insulting for an administrator looking
 * at the only company they will ever have. No chevron says "there is nothing to
 * open", which is true, and the value stays perfectly legible instead of being
 * dimmed to 50% for no reason.
 *
 * ── The keyboard ────────────────────────────────────────────────────────────
 *
 * Every option is a real `<button>`, so Enter and Space choose one and Tab still
 * walks the list. On top of that: Escape closes and returns focus to the trigger
 * (`usePopover`), and Up/Down move focus through the options from anywhere in
 * the panel - including from the search box, which is where focus lands when a
 * searchable panel opens. That is the whole of it. There is no type-ahead and no
 * `aria-activedescendant`; see `moveOptionFocus` for why real focus rather than
 * a virtual cursor.
 */
export interface FilterOption {
  value: string;
  label: string;
  /** A second line under the label. Keep it short; it is context, not prose. */
  hint?: string;
  /**
   * A heading rendered above this option. Consecutive options sharing a group
   * render under one heading, in the order the array already gives them - so
   * grouping is a property of how you sorted the list, not a nested structure
   * to build.
   */
  group?: string;
  /**
   * Shown but not choosable. For a value that exists and is unavailable, where
   * hiding it leaves the reader hunting for something they know is there.
   */
  disabled?: boolean;
  icon?: React.ReactNode;
}

/**
 * Above this many options, the panel gets a search box on its own.
 *
 * Six is roughly where a list stops being scannable in one look. Below it a
 * search box is a control to tab past; above it, its absence is what makes a
 * company with forty sites unusable.
 */
const SEARCH_THRESHOLD = 6;

/**
 * How tall the panel may get, whatever room the viewport leaves below it.
 *
 * Roughly eight options plus the search box. Beyond that the list stops reading
 * as a menu and starts reading as a page, and on a tall window a picker anchored
 * near the top would otherwise run most of the way down the screen. `.fsel__list`
 * scrolls inside it; the search box and the group headings stay put.
 */
const PANEL_MAX_HEIGHT = 320;

export function FilterSelect({
  value,
  options,
  onChange,
  label,
  icon,
  placeholder = "Any",
  /** Marks the pill as carrying a real choice rather than its default. */
  active,
  align = "start",
  variant = "pill",
  searchable,
  onQueryChange,
  searchPlaceholder = "Search",
  emptyLabel = "No matches",
  invalid,
  disabled,
  id,
  className,
}: {
  value: string;
  options: readonly FilterOption[];
  onChange: (value: string) => void;
  /** The accessible name. The trigger only shows the value. */
  label: string;
  icon?: React.ReactNode;
  placeholder?: string;
  active?: boolean;
  align?: "start" | "end";
  variant?: "pill" | "chip" | "field";
  /** Force the search box on or off. Defaults to "on past six options". */
  searchable?: boolean;
  /**
   * Report the search term instead of filtering on it.
   *
   * For a picker whose options are a page of a much larger set. Filtering the
   * hundred rows that happen to have arrived looks identical to filtering the
   * whole table right up to the moment the row somebody wants is number 140, and
   * then it looks like the record does not exist. A parent that passes this
   * refetches, and its `options` become the answer - so local filtering is
   * skipped, or the panel would filter the results twice and hide anything the
   * server matched on a field the label does not show.
   */
  onQueryChange?: (query: string) => void;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /** Field validation. `pill` and `chip` have no invalid state to be in. */
  invalid?: boolean;
  /** Genuinely refused, not merely fixed - see the header on the two. */
  disabled?: boolean;
  /** So a `Field`'s `<label htmlFor>` reaches the trigger. */
  id?: string;
  className?: string;
}) {
  const { open, toggle, close, rootRef, triggerRef, panelRef, panelStyle, panelTarget } =
    usePopover({
      align,
      matchTriggerWidth: true,
      maxHeight: PANEL_MAX_HEIGHT,
    });
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);
  /**
   * One option is not a choice. See the header comment: this reads as fixed,
   * never as disabled.
   *
   * The `selected` half matters. A list of exactly one option that is **not** the
   * current value is still a choice - the one nobody has made yet - and locking
   * it shut leaves a form showing "Select a site…" with no way to select one.
   * That is reachable the moment a form defaults its value from somewhere other
   * than the list it is offering, which the invite dialog does: its site defaults
   * to the console's active site, which need not be in the company being invited
   * into.
   */
  const fixed = !disabled && options.length <= 1 && (options.length === 0 || Boolean(selected));
  // A server-searched picker always gets the box: "past six options" counts the
  // page in hand, and a second page is precisely the case that needs searching.
  const showSearch = searchable ?? (Boolean(onQueryChange) || options.length > SEARCH_THRESHOLD);

  const changeQuery = React.useCallback(
    (next: string) => {
      setQuery(next);
      onQueryChange?.(next);
    },
    [onQueryChange]
  );

  const matches = React.useMemo(() => {
    // The parent is doing the searching, so whatever it handed us is the result.
    if (onQueryChange) return options;

    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        (option.hint ?? "").toLowerCase().includes(term)
    );
  }, [options, query, onQueryChange]);

  // The query is per-opening. Carrying it over means reopening a panel that
  // silently hides most of its options, with the reason two lines above the
  // fold in a box the user did not put anything into this time.
  React.useEffect(() => {
    if (open) {
      changeQuery("");
      if (showSearch) {
        // After the layout effect that places the panel, so focusing it cannot
        // scroll a panel that has not been positioned yet.
        const frame = requestAnimationFrame(() => searchRef.current?.focus());
        return () => cancelAnimationFrame(frame);
      }
    }
    // `changeQuery` is deliberately not a dependency. It is only stable if the
    // parent memoised `onQueryChange`, and an inline arrow would make this
    // effect re-run on every render - clearing the box under whoever is typing
    // in it. Opening is the only thing that should reset the term.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showSearch]);

  function onPanelKeyDown(event: React.KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveOptionFocus(panelRef.current, event.key === "ArrowDown" ? 1 : -1);
    }
  }

  return (
    <div ref={rootRef} className={cn("fsel", variant === "field" && "fsel--field", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup={fixed ? undefined : "listbox"}
        aria-expanded={fixed ? undefined : open}
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        // The trigger is fixed-width for the chip variant, so a long name is
        // ellipsed. The full one has to stay reachable without opening anything.
        title={selected?.label}
        onClick={() => !fixed && toggle()}
        onKeyDown={(event) => {
          // Down opens onto the list rather than merely opening. On a control
          // whose whole purpose is picking from a list, one key should get you
          // into it.
          if (!fixed && !open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            toggle();
          }
        }}
        className={cn(
          "fsel__trigger",
          variant === "chip" && "fsel__trigger--chip",
          variant === "field" && "fsel__trigger--field",
          active && "fsel__trigger--active",
          invalid && "fsel__trigger--invalid",
          fixed && "fsel__trigger--fixed"
        )}
      >
        {icon && <span className="fsel__icon">{icon}</span>}
        <span className={cn("fsel__value", !selected && "fsel__value--placeholder")}>
          {selected?.label ?? placeholder}
        </span>
        {!fixed && (
          <ChevronDown aria-hidden className={cn("fsel__chevron", open && "fsel__chevron--open")} />
        )}
      </button>

      {open &&
        !fixed &&
        createPortal(
          <div
            ref={panelRef}
            // See the "top layer" note in `usePopover.ts` - this is what lets
            // the panel render over an open `Modal`/`Drawer`.
            popover="manual"
            role="listbox"
            aria-label={label}
            style={panelStyle}
            onKeyDown={onPanelKeyDown}
            className="fsel__panel"
          >
            {showSearch && (
              <div className="fsel__search">
                <Search aria-hidden className="fsel__search-icon" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  aria-label={`Search ${label}`}
                  placeholder={searchPlaceholder}
                  onChange={(event) => changeQuery(event.target.value)}
                  className="fsel__search-input"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      changeQuery("");
                      searchRef.current?.focus();
                    }}
                    className="fsel__search-clear"
                  >
                    <X aria-hidden />
                  </button>
                )}
              </div>
            )}

            <div className="fsel__list">
              {matches.length === 0 && <p className="fsel__empty">{emptyLabel}</p>}

              {matches.map((option, index) => {
                const isSelected = option.value === value;
                // Headings are driven by the neighbour rather than by a nested
                // shape, so a filtered list re-heads itself for free.
                const heading = option.group && option.group !== matches[index - 1]?.group;

                return (
                  <React.Fragment key={option.value}>
                    {heading && <p className="fsel__group">{option.group}</p>}
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      onClick={() => {
                        onChange(option.value);
                        close(true);
                      }}
                      className={cn("fsel__option", isSelected && "fsel__option--selected")}
                    >
                      {option.icon && <span className="fsel__option-icon">{option.icon}</span>}
                      <span className="fsel__option-text">
                        <span className="fsel__option-label">{option.label}</span>
                        {option.hint && <span className="fsel__option-hint">{option.hint}</span>}
                      </span>
                      {isSelected && <Check aria-hidden className="fsel__check" />}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>,
          panelTarget
        )}
    </div>
  );
}
